import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Timeline } from 'vis-timeline/standalone';
import { DataSet } from 'vis-data';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, IconButton, Toolbar, Typography, Divider, Chip, Select, MenuItem, FormControl, InputLabel, Grid, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import MergeIcon from '@mui/icons-material/MergeType';
import DeleteIcon from '@mui/icons-material/Delete';
import AddBoxIcon from '@mui/icons-material/AddBox';
import SaveIcon from '@mui/icons-material/Save';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import api from '../utils/apiClient';
import { DEVICE_API as API_BASE_URL } from '../utils/apiConfig';
import 'vis-timeline/styles/vis-timeline-graph2d.css';

const DeviceStatusTimeline = ({ open, onClose, devices, selectedDeviceIds, uptimeData, showSuccess, showError, showInfo, onDataRefresh }) => {
  const { t, i18n } = useTranslation('device');
  const dayjsLocale = i18n.language === 'zh' ? 'zh-cn' : i18n.language === 'en' ? 'en' : 'ru';
  const theme = useTheme();
  const timelineRef = useRef(null);
  const timelineInstance = useRef(null);
  const [selectedItems, setSelectedItems] = useState([]); // Для отслеживания выбранных элементов
  const isShiftPressedRef = useRef(false); // Используем ref вместо state для избежания перерендера
  const [isShiftPressedState, setIsShiftPressedState] = useState(false); // Только для UI индикатора
  
  // State для диалога массового создания
  const [openBulkCreateDialog, setOpenBulkCreateDialog] = useState(false);
  const [bulkCreateData, setBulkCreateData] = useState({
    startTime: dayjs(),
    endTime: dayjs().add(1, 'hour'),
    statusType: 'online'
  });

  // State для отслеживания статуса "Removed" у устройств
  const [devicesRemovedStatus, setDevicesRemovedStatus] = useState({});

  // State для загрузки данных uptime
  const [isLoadingUptime, setIsLoadingUptime] = useState(false);
  const [localUptimeData, setLocalUptimeData] = useState([]);

  // Для отслеживания изменения размера группы элементов
  const resizeStartData = useRef(null);
  
  // Для отслеживания ведущего элемента при групповом перемещении
  const leaderItemData = useRef(null); // { id, originalStart, originalEnd, lastProcessedStart, magnetShift }
  const isGroupMoving = useRef(false);
  
  // Для отслеживания изменения размера через интервал
  const resizeCheckInterval = useRef(null);
  const lastKnownSizes = useRef({});
  
  // Сохраняем snapshot данных при открытии диалога
  const [snapshotDevices, setSnapshotDevices] = useState([]);
  const [snapshotUptimeData, setSnapshotUptimeData] = useState([]);
  const prevOpenRef = useRef(false);

  // Система отслеживания изменений
  const [changes, setChanges] = useState({
    created: [],    // Новые элементы { device_id, status, started_at, ended_at }
    updated: [],    // Измененные элементы { id, started_at, ended_at, status }
    deleted: []     // Удаленные элементы { id }
  });
  const [isSaving, setIsSaving] = useState(false);
  const originalItemsRef = useRef({}); // Хранит оригинальные данные элементов из БД

  // Загрузка данных uptime при открытии диалога
  useEffect(() => {
    const fetchUptimeData = async () => {
      if (!open || !selectedDeviceIds || selectedDeviceIds.length === 0) {
        return;
      }

      // Если данные уже переданы через props и не пустые, используем их
      if (uptimeData && uptimeData.length > 0) {
        setLocalUptimeData(uptimeData);
        return;
      }

      // Иначе загружаем данные самостоятельно
      setIsLoadingUptime(true);
      try {
        // Определяем диапазон дат (последние 30 дней)
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);

        const deviceLimit = 500;
        let deviceOffset = 0;
        let allCharts = [];
        let hasMoreDevices = true;

        while (hasMoreDevices) {
          const response = await api.get(`${API_BASE_URL}/dashboard/charts`, {
            params: {
              start_date: startDate.toISOString(),
              end_date: endDate.toISOString(),
              device_limit: deviceLimit,
              device_offset: deviceOffset,
            },
          });

          if (response.data && Array.isArray(response.data)) {
            allCharts = [...allCharts, ...response.data];

            if (response.data.length < deviceLimit) {
              hasMoreDevices = false;
            } else {
              deviceOffset += deviceLimit;
            }
          } else {
            hasMoreDevices = false;
          }
        }

        console.log('✅ Загружены данные uptime:', {
          total: allCharts.length,
          sample: allCharts[0]
        });

        setLocalUptimeData(allCharts);
      } catch (error) {
        console.error('❌ Ошибка загрузки данных uptime:', error);
        if (showError) {
          showError(t('timelineLoadError'));
        }
        setLocalUptimeData([]);
      } finally {
        setIsLoadingUptime(false);
      }
    };

    fetchUptimeData();
  }, [open, selectedDeviceIds]);

  // Создаем snapshot ТОЛЬКО при открытии диалога (когда open меняется с false на true)
  useEffect(() => {
    // Если диалог только что открылся (был закрыт, стал открытым)
    if (open && !prevOpenRef.current) {
      console.log('📊 DeviceStatusTimeline открылся', {
        devices: devices?.length || 0,
        uptimeData: uptimeData?.length || 0,
        localUptimeData: localUptimeData?.length || 0,
        selectedDeviceIds: selectedDeviceIds?.length || 0,
        uptimeDataSample: uptimeData?.[0],
        localUptimeDataSample: localUptimeData?.[0]
      });

      // Используем localUptimeData если uptimeData пустой
      const dataToUse = (uptimeData && uptimeData.length > 0) ? uptimeData : localUptimeData;

      // Глубокое копирование данных, чтобы они не менялись при обновлении родителя
      setSnapshotDevices(JSON.parse(JSON.stringify(devices || [])));
      setSnapshotUptimeData(JSON.parse(JSON.stringify(dataToUse || [])));

      // Сбрасываем изменения
      setChanges({ created: [], updated: [], deleted: [] });

      // Сохраняем оригинальные данные элементов из БД
      originalItemsRef.current = {};
    }

    // Если диалог закрылся, очищаем snapshot
    if (!open && prevOpenRef.current) {
      setSnapshotDevices([]);
      setSnapshotUptimeData([]);
      setChanges({ created: [], updated: [], deleted: [] });
      originalItemsRef.current = {};
    }

    // Обновляем ref для следующей проверки
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Зависим только от open, берем devices и uptimeData из замыкания

  // Обновляем snapshot когда данные обновляются (после сохранения)
  useEffect(() => {
    // Обновляем только если диалог открыт
    const dataToCheck = (uptimeData && uptimeData.length > 0) ? uptimeData : localUptimeData;

    if (open && dataToCheck && dataToCheck.length > 0) {
      // Проверяем, действительно ли данные изменились
      const currentDataStr = JSON.stringify(dataToCheck);
      const snapshotDataStr = JSON.stringify(snapshotUptimeData);

      if (currentDataStr !== snapshotDataStr && snapshotUptimeData.length > 0) {
        console.log('🔄 Обновление snapshot данных после изменений');

        // Обновляем snapshot
        setSnapshotDevices(JSON.parse(JSON.stringify(devices || [])));
        setSnapshotUptimeData(JSON.parse(JSON.stringify(dataToCheck || [])));

        // Сбрасываем изменения после обновления
        setChanges({ created: [], updated: [], deleted: [] });

        // Очищаем originalItemsRef - он будет заполнен заново при создании timeline
        originalItemsRef.current = {};
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uptimeData, localUptimeData, open]);

  // Отслеживание нажатия Shift для магнитного прилипания
  useEffect(() => {
    if (!open) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Shift' && !isShiftPressedRef.current) {
        isShiftPressedRef.current = true;
        setIsShiftPressedState(true); // Только для UI индикатора
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.key === 'Shift') {
        isShiftPressedRef.current = false;
        setIsShiftPressedState(false); // Только для UI индикатора
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      isShiftPressedRef.current = false;
      setIsShiftPressedState(false);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !timelineRef.current || snapshotDevices.length === 0) return;

    // Очищаем предыдущий экземпляр
    if (timelineInstance.current) {
      timelineInstance.current.destroy();
      timelineInstance.current = null;
    }

    // Фильтруем только выбранные устройства из snapshot
    const selectedDevices = snapshotDevices.filter(device => 
      selectedDeviceIds.includes(device.id)
    );

    // Создаем группы (2 дорожки для каждого устройства: основной статус + дополнительный)
    const groups = new DataSet(
      selectedDevices.flatMap((device, index) => {
        const deviceName = device.model || device.ip_address || device.ip || `Device ${index + 1}`;
        return [
          {
            id: `${device.id}_main`,
            content: `${deviceName} (Online/Offline)`,
            order: index * 2,
            deviceId: device.id,
            trackType: 'main'
          },
          {
            id: `${device.id}_additional`,
            content: `${deviceName} (Repair/Special)`,
            order: index * 2 + 1,
            deviceId: device.id,
            trackType: 'additional',
            style: 'background-color: rgba(150, 150, 150, 0.1);' // Визуально отличаем дополнительную дорожку
          }
        ];
      })
    );

    // Создаем элементы (статусы) из snapshot данных
    const items = new DataSet(generateStatusDataFromAPI(selectedDevices, snapshotUptimeData));

    // Опции timeline
    const options = {
      width: '100%',
      height: '100%',
      stack: false,
      multiselect: true, // Включаем множественный выбор (Ctrl/Cmd + клик)
      multiselectPerGroup: false, // Разрешаем выбор элементов из разных групп
      editable: {
        add: true,
        updateTime: true,
        updateGroup: false,  // Запрещаем перемещение между группами
        remove: false,  // Отключаем крестики - удаляем через кнопку
        overrideItems: false
      },
      groupEditable: false,  // Дополнительная защита от изменения группы
      snap: function(date, scale, step) {
        // Привязка к минутам (округляем до ближайшей минуты)
        const minute = 1000 * 60;
        return Math.round(date / minute) * minute;
      },
      onAdd: function (item, callback) {
        // Диалог для добавления нового статуса
        const content = window.prompt(t('timelinePromptStatus'), 'offline');
        if (content !== null) {
          item.content = content;
          item.className = content.toLowerCase();
          // Сохраняем оригинальную группу для защиты от перемещения
          item.originalGroup = item.group;
          callback(item);
        } else {
          callback(null);
        }
      },
      onMoving: function (item, callback) {
        // Проверка на существование item
        if (!item || !callback) {
          if (callback) callback(item);
          return;
        }
        
        // КРИТИЧЕСКИ ВАЖНО: Жестко фиксируем элемент на его строке
        // Используем сохраненное поле originalGroup из элемента
        if (item.originalGroup) {
          item.group = item.originalGroup;
        }
        
        // Получаем текущее выделение
        const selection = timelineInstance.current ? timelineInstance.current.getSelection() : [];
        const isMultiSelection = selection && selection.length > 1 && selection.includes(item.id);
        
        // Инициализация при первом вызове onMoving
        if (!leaderItemData.current) {
          const itemStart = new Date(item.start).getTime();
          const itemEnd = new Date(item.end).getTime();
          
          // Сохраняем оригинальные позиции ВСЕХ выбранных элементов
          const allOriginals = {};
          if (isMultiSelection) {
            selection.forEach(id => {
              const selectedItem = items.get(id);
              if (selectedItem) {
                allOriginals[id] = {
                  start: new Date(selectedItem.start).getTime(),
                  end: new Date(selectedItem.end).getTime(),
                  group: selectedItem.group
                };
              }
            });
          } else {
            // Для одиночного элемента
            allOriginals[item.id] = {
              start: itemStart,
              end: itemEnd,
              group: item.group
            };
          }
          
          leaderItemData.current = {
            id: isMultiSelection ? null : item.id,  // Для одиночного элемента сразу определяем как ведущий
            originalStart: itemStart,
            originalEnd: itemEnd,
            originalGroup: item.originalGroup || item.group,
            allOriginals: allOriginals,
            currentStart: itemStart,
            currentEnd: itemEnd,
            duration: itemEnd - itemStart,
            magnetShift: 0
          };
          isGroupMoving.current = true;
        }
        
        // КЛЮЧЕВОЙ МОМЕНТ: Определяем ведущего по изменению позиции
        // Ведущий - это элемент, который изменил позицию относительно оригинальной
        let isLeader = false;
        if (isMultiSelection && leaderItemData.current.allOriginals[item.id]) {
          const itemStart = new Date(item.start).getTime();
          const origStart = leaderItemData.current.allOriginals[item.id].start;
          const positionChanged = Math.abs(itemStart - origStart) > 100; // Сдвиг больше 100мс
          
          // Если позиция изменилась и ведущий еще не определен - это ведущий
          if (positionChanged && !leaderItemData.current.id) {
            leaderItemData.current.id = item.id;
            leaderItemData.current.originalStart = origStart;
            leaderItemData.current.originalEnd = leaderItemData.current.allOriginals[item.id].end;
            leaderItemData.current.originalGroup = item.originalGroup || item.group;
            isLeader = true;
          } else {
            isLeader = item.id === leaderItemData.current.id;
          }
        } else {
          isLeader = item.id === leaderItemData.current.id;
        }
        
        // Если это ведущий элемент, сохраняем его текущую позицию СРАЗУ
        if (isLeader) {
          const currentStart = new Date(item.start).getTime();
          const currentEnd = new Date(item.end).getTime();
          
          leaderItemData.current.currentStart = currentStart;
          leaderItemData.current.currentEnd = currentEnd;
          leaderItemData.current.duration = currentEnd - currentStart;
          
          // Примечание: изменение размера (resize) теперь обрабатывается через постоянный мониторинг
        }
        
        // МОНОЛИТНОЕ ПЕРЕМЕЩЕНИЕ: Ведомые элементы синхронно следуют за ведущим
        if (!isLeader && isMultiSelection && leaderItemData.current.allOriginals[item.id] && leaderItemData.current.id) {
          // Вычисляем изменения ведущего элемента
          const startChange = leaderItemData.current.currentStart - leaderItemData.current.originalStart;
          const endChange = leaderItemData.current.currentEnd - leaderItemData.current.originalEnd;
          
          // Применяем те же изменения к ведомому элементу
          const followerOrig = leaderItemData.current.allOriginals[item.id];
          item.start = new Date(followerOrig.start + startChange);
          item.end = new Date(followerOrig.end + endChange);
          item.group = followerOrig.group;
          
          callback(item);
          return;
        }
        
        // Магнитное прилипание при зажатом Shift (только для одного элемента)
        const shiftPressed = isShiftPressedRef.current;
        
        // Магнит работает только если выбран ОДИН элемент
        if (shiftPressed && timelineInstance.current && isLeader && !isMultiSelection) {
          try {
            const allItems = items.get();
            if (!allItems || !Array.isArray(allItems)) {
              callback(item);
              return;
            }
            
            // Получаем текущий zoom level для адаптивного радиуса
            const window = timelineInstance.current.getWindow();
            const zoomRange = window.end - window.start; // Видимый диапазон в миллисекундах
            const zoomInHours = zoomRange / (1000 * 60 * 60); // Диапазон в часах
            
            // Динамический радиус прилипания в зависимости от масштаба
            // Чем больше зум (меньше видимый диапазон), тем меньше радиус
            // Чем меньше зум (больше видимый диапазон), тем больше радиус
            let snapThreshold;
            if (zoomInHours < 6) {
              snapThreshold = 5 * 60 * 1000; // 5 минут для детального вида
            } else if (zoomInHours < 24) {
              snapThreshold = 15 * 60 * 1000; // 15 минут для дневного вида
            } else if (zoomInHours < 168) { // неделя
              snapThreshold = 60 * 60 * 1000; // 1 час для недельного вида
            } else {
              snapThreshold = 4 * 60 * 60 * 1000; // 4 часа для месячного вида
            }
            
            // Находим все элементы из ВСЕХ групп (строк), кроме текущего
            // Магнит работает по всем строкам, но элемент остается в своей строке
            const otherItems = allItems.filter(i => 
              i && i.id !== item.id // Все элементы кроме текущего
            );
            
            const itemStart = new Date(item.start).getTime();
            const itemEnd = new Date(item.end).getTime();
            const duration = itemEnd - itemStart;
            
            let bestSnapStart = null;
            let bestSnapEnd = null;
            let minStartDiff = snapThreshold;
            let minEndDiff = snapThreshold;
            
            // Проверяем все элементы со всех строк
            otherItems.forEach(otherItem => {
              const otherStart = new Date(otherItem.start).getTime();
              const otherEnd = new Date(otherItem.end).getTime();
              
              // Прилипание начала текущего элемента к концу другого
              const diffStartToEnd = Math.abs(itemStart - otherEnd);
              if (diffStartToEnd < minStartDiff) {
                minStartDiff = diffStartToEnd;
                bestSnapStart = otherEnd;
              }
              
              // Прилипание начала текущего к началу другого
              const diffStartToStart = Math.abs(itemStart - otherStart);
              if (diffStartToStart < minStartDiff) {
                minStartDiff = diffStartToStart;
                bestSnapStart = otherStart;
              }
              
              // Прилипание конца текущего к началу другого
              const diffEndToStart = Math.abs(itemEnd - otherStart);
              if (diffEndToStart < minEndDiff) {
                minEndDiff = diffEndToStart;
                bestSnapEnd = otherStart;
              }
              
              // Прилипание конца текущего к концу другого
              const diffEndToEnd = Math.abs(itemEnd - otherEnd);
              if (diffEndToEnd < minEndDiff) {
                minEndDiff = diffEndToEnd;
                bestSnapEnd = otherEnd;
              }
            });
          
            // Применяем магнитное прилипание
            // Приоритет: сначала пытаемся прилепить начало, если не получается - конец
            if (bestSnapStart !== null && minStartDiff < snapThreshold) {
              const newStart = bestSnapStart;
              const newEnd = bestSnapStart + duration;
              
              // Вычисляем сдвиг от магнита
              const magnetShift = newStart - itemStart;
              
              // Накапливаем сдвиг от магнита
              leaderItemData.current.magnetShift += magnetShift;
              
              item.start = new Date(newStart);
              item.end = new Date(newEnd);
              
              // Обновляем lastProcessedStart
              leaderItemData.current.lastProcessedStart = newStart;
              leaderItemData.current.lastProcessedEnd = newEnd;
            } else if (bestSnapEnd !== null && minEndDiff < snapThreshold) {
              const newEnd = bestSnapEnd;
              const newStart = bestSnapEnd - duration;
              
              // Вычисляем сдвиг от магнита
              const magnetShift = newStart - itemStart;
              
              // Накапливаем сдвиг от магнита
              leaderItemData.current.magnetShift += magnetShift;
              
              item.end = new Date(newEnd);
              item.start = new Date(newStart);
              
              // Обновляем lastProcessedStart
              leaderItemData.current.lastProcessedStart = newStart;
              leaderItemData.current.lastProcessedEnd = newEnd;
            }
          } catch (error) {
            // Ошибка в магнитном прилипании
          }
        }
        
        // Обновляем последнюю обработанную позицию ведущего элемента ДО callback
        // чтобы ведомые элементы использовали актуальную позицию
        if (isLeader) {
          const currentStart = new Date(item.start).getTime();
          const currentEnd = new Date(item.end).getTime();
          
          // Обновляем lastProcessedStart для использования ведомыми элементами
          leaderItemData.current.lastProcessedStart = currentStart;
          leaderItemData.current.lastProcessedEnd = currentEnd;
        }
        
        callback(item);
      },
      onMove: function (item, callback) {
        // КРИТИЧЕСКИ ВАЖНО: проверяем, что группа не изменилась
        // Используем сохраненное поле originalGroup из самого элемента
        const originalGroup = item.originalGroup;
        
        // Если группа изменилась - отклоняем изменение
        if (originalGroup && item.group !== originalGroup) {
          callback(null); // Отклоняем изменение
          // Сбрасываем данные
          leaderItemData.current = null;
          isGroupMoving.current = false;
          return;
        }
        
        // Сбрасываем данные ведущего элемента при завершении перемещения
        leaderItemData.current = null;
        isGroupMoving.current = false;
        callback(item);
      },
      onUpdate: function (item, callback) {
        // Проверяем, что группа не изменилась, используя сохраненное поле originalGroup
        const originalGroup = item.originalGroup;
        
        if (originalGroup && item.group !== originalGroup) {
          // Возвращаем оригинальную группу
          item.group = originalGroup;
        }
        
        callback(item);
      },
      onRemove: function (item, callback) {
        if (window.confirm(t('timelineDeleteConfirm'))) {
          callback(item);
        } else {
          callback(null);
        }
      },
      groupOrder: 'order',
      orientation: 'top',
      margin: {
        item: 10,
        axis: 5
      },
      zoomMin: 1000 * 60 * 60, // 1 час минимум
      zoomMax: 1000 * 60 * 60 * 24 * 365, // 1 год максимум
    };

    // Создаем timeline
    timelineInstance.current = new Timeline(
      timelineRef.current,
      items,
      groups,
      options
    );

    // Применяем темную тему если нужно
    if (theme.palette.mode === 'dark') {
      timelineRef.current.style.backgroundColor = theme.palette.background.paper;
      timelineRef.current.style.color = theme.palette.text.primary;
    }

    // Обработчики событий
    timelineInstance.current.on('select', (properties) => {
      setSelectedItems(properties.items || []);
    });

    // Обработчик изменения элементов (перемещение и изменение размера)
    timelineInstance.current.on('changed', (properties) => {
      // Проверяем наличие properties и items
      if (!properties || !properties.items || properties.items.length === 0) {
        return;
      }
      
      try {
        const changedItem = items.get(properties.items[0]);
        if (!changedItem) return;
        
        const selection = timelineInstance.current.getSelection();
        if (!selection || selection.length <= 1) {
          // Сбрасываем для одиночных элементов
          resizeStartData.current = null;
          return;
        }
        
        // Групповое изменение размера: если выбрано несколько элементов
        if (selection.includes(changedItem.id)) {
          // Инициализация: сохраняем начальное состояние всех выбранных элементов
          if (!resizeStartData.current) {
            resizeStartData.current = {};
            selection.forEach(id => {
              const item = items.get(id);
              if (item) {
                resizeStartData.current[id] = {
                  start: new Date(item.start).getTime(),
                  end: new Date(item.end).getTime(),
                  duration: new Date(item.end).getTime() - new Date(item.start).getTime()
                };
              }
            });
          }
          
          // Вычисляем изменения
          const originalData = resizeStartData.current[changedItem.id];
          if (originalData) {
            // Обработка теперь в onMoving, здесь можно оставить как fallback
            // но основная логика перенесена туда, где она срабатывает надежнее
          }
        }
      } catch (error) {
        resizeStartData.current = null;
      }
    });

    // ПОСТОЯННЫЙ мониторинг изменений для множественного выбора
    // Запускаем интервал, который постоянно проверяет, изменился ли размер элементов
    const startContinuousTracking = () => {
      resizeCheckInterval.current = setInterval(() => {
        const selection = timelineInstance.current ? timelineInstance.current.getSelection() : [];
        
        // Если выбрано меньше 2 элементов - очищаем lastKnownSizes
        if (!selection || selection.length <= 1) {
          if (Object.keys(lastKnownSizes.current).length > 0) {
            lastKnownSizes.current = {};
          }
          return;
        }
        
        // Инициализируем lastKnownSizes для новых элементов
        selection.forEach(id => {
          if (!lastKnownSizes.current[id]) {
            const item = items.get(id);
            if (item) {
              lastKnownSizes.current[id] = {
                start: new Date(item.start).getTime(),
                end: new Date(item.end).getTime()
              };
            }
          }
        });
        
        // Проверяем каждый элемент на изменения
        let hasChanges = false;
        selection.forEach(id => {
          const item = items.get(id);
          const lastKnown = lastKnownSizes.current[id];
          
          if (item && lastKnown) {
            const currentStart = new Date(item.start).getTime();
            const currentEnd = new Date(item.end).getTime();
            
            const startChange = currentStart - lastKnown.start;
            const endChange = currentEnd - lastKnown.end;
            
            // Если размер этого элемента изменился
            if (Math.abs(startChange) > 100 || Math.abs(endChange) > 100) {
              if (!hasChanges) {
                hasChanges = true;
              }
              
              // Применяем такое же изменение ко всем остальным выделенным элементам
              selection.forEach(otherId => {
                if (otherId !== id && lastKnownSizes.current[otherId]) {
                  const otherLast = lastKnownSizes.current[otherId];
                  items.update({
                    id: otherId,
                    start: new Date(otherLast.start + startChange),
                    end: new Date(otherLast.end + endChange)
                  });
                  
                  // Обновляем lastKnown для синхронизированного элемента
                  lastKnownSizes.current[otherId] = {
                    start: otherLast.start + startChange,
                    end: otherLast.end + endChange
                  };
                }
              });
              
              // Обновляем lastKnown для изменившегося элемента
              lastKnownSizes.current[id] = {
                start: currentStart,
                end: currentEnd
              };
            }
          }
        });
      }, 50); // Проверка каждые 50мс
    };
    
    const stopContinuousTracking = () => {
      if (resizeCheckInterval.current) {
        clearInterval(resizeCheckInterval.current);
        resizeCheckInterval.current = null;
        lastKnownSizes.current = {};
      }
    };
    
    // Запускаем постоянный мониторинг
    startContinuousTracking();

    // Cleanup
    return () => {
      stopContinuousTracking();
      if (timelineInstance.current) {
        timelineInstance.current.destroy();
        timelineInstance.current = null;
      }
    };
  }, [open, snapshotDevices, snapshotUptimeData, selectedDeviceIds, theme.palette.mode]);

  // Генерация данных из реального API
  const generateStatusDataFromAPI = (selectedDevices, uptimeData) => {
    const items = [];

    if (!uptimeData || !Array.isArray(uptimeData)) {
      return items;
    }

    selectedDevices.forEach((device, deviceIndex) => {
      const deviceId = device.id;

      // Находим данные uptime для этого устройства
      const deviceUptimeData = uptimeData.find(data => data.device_id === deviceId);

      if (!deviceUptimeData || !Array.isArray(deviceUptimeData.uptime)) {
        return;
      }

      // Debug: проверяем структуру данных uptime
      if (deviceIndex === 0) {
        console.log('✅ Структура uptime данных для первого устройства:', {
          deviceId,
          uptimeRecordsCount: deviceUptimeData.uptime.length,
          firstRecord: deviceUptimeData.uptime[0],
          hasIdField: !!deviceUptimeData.uptime[0]?.id,
          idValue: deviceUptimeData.uptime[0]?.id
        });
      }

      // Создаем элементы для каждого периода uptime
      deviceUptimeData.uptime.forEach((uptimeRecord, index) => {
        const start = new Date(uptimeRecord.started_at);
        const end = uptimeRecord.ended_at ? new Date(uptimeRecord.ended_at) : new Date();

        // Нормализуем статус (нечувствительность к регистру)
        let status;
        let trackType;
        let groupId;

        // Обрабатываем boolean статусы (для обратной совместимости)
        if (uptimeRecord.status === true) {
          status = 'online';
          trackType = 'main';
          groupId = `${deviceId}_main`;
        } else if (uptimeRecord.status === false) {
          status = 'offline';
          trackType = 'main';
          groupId = `${deviceId}_main`;
        } else if (typeof uptimeRecord.status === 'string') {
          // Нормализуем строковый статус
          const normalizedStatus = uptimeRecord.status.trim().toLowerCase();
          status = normalizedStatus;

          // Определяем дорожку: online/offline на основной, остальные на дополнительной
          if (normalizedStatus === 'online' || normalizedStatus === 'offline') {
            trackType = 'main';
            groupId = `${deviceId}_main`;
          } else {
            // Все остальные статусы (repair, maintenance и т.д.) на дополнительной дорожке
            trackType = 'additional';
            groupId = `${deviceId}_additional`;
          }
        } else {
          // Неизвестный тип статуса, считаем offline
          status = 'offline';
          trackType = 'main';
          groupId = `${deviceId}_main`;
        }

        const itemId = `${deviceId}_${trackType}_${index}`;

        // Сохраняем оригинальные данные из БД для отслеживания изменений
        originalItemsRef.current[itemId] = {
          dbId: uptimeRecord.id, // ID записи в БД (может быть undefined, если нет в API)
          device_id: deviceId,
          status: status,
          started_at: new Date(start).toISOString(),
          ended_at: end ? new Date(end).toISOString() : null
        };

        // Debug: проверяем сохранение первой записи
        if (deviceIndex === 0 && index === 0) {
          console.log('✅ Сохранение originalItemsRef для первой записи:', {
            itemId,
            data: originalItemsRef.current[itemId],
            hasDbId: !!originalItemsRef.current[itemId].dbId,
            dbIdValue: originalItemsRef.current[itemId].dbId
          });
        }

        items.push({
          id: itemId,
          group: groupId,
          originalGroup: groupId, // ВАЖНО: сохраняем оригинальную группу для защиты от перемещения
          content: status.toUpperCase(),
          start: start,
          end: end,
          className: status,
          editable: true,
          type: 'range',
          deviceId: deviceId,
          trackType: trackType
        });
      });
    });

    return items;
  };

  const handleZoomIn = () => {
    if (timelineInstance.current) {
      timelineInstance.current.zoomIn(0.5);
    }
  };

  const handleZoomOut = () => {
    if (timelineInstance.current) {
      timelineInstance.current.zoomOut(0.5);
    }
  };

  const handleFit = () => {
    if (timelineInstance.current) {
      timelineInstance.current.fit();
    }
  };

  // Функция для автоматического склеивания соседних одинаковых статусов
  const handleMergeAdjacentItems = () => {
    if (!timelineInstance.current) return;
    
    const items = timelineInstance.current.itemsData;
    const allItems = items.get();
    
    // Группируем элементы по группам (дорожкам)
    const itemsByGroup = {};
    allItems.forEach(item => {
      if (!itemsByGroup[item.group]) {
        itemsByGroup[item.group] = [];
      }
      itemsByGroup[item.group].push(item);
    });
    
    let mergedCount = 0;
    
    // Обрабатываем каждую группу отдельно
    Object.keys(itemsByGroup).forEach(groupId => {
      const groupItems = itemsByGroup[groupId].sort((a, b) => new Date(a.start) - new Date(b.start));
      
      for (let i = 0; i < groupItems.length - 1; i++) {
        const current = groupItems[i];
        const next = groupItems[i + 1];
        
        // Проверяем, одинаковый ли тип статуса и вплотную ли они стоят
        if (current.className === next.className) {
          const currentEnd = new Date(current.end);
          const nextStart = new Date(next.start);
          const gap = (nextStart - currentEnd) / 1000; // Разница в секундах
          
          // Если разница меньше 60 секунд, склеиваем
          if (gap <= 60) {
            // Объединяем: расширяем текущий элемент до конца следующего
            items.update({
              id: current.id,
              end: next.end
            });
            
            // Удаляем следующий элемент
            items.remove(next.id);
            
            mergedCount++;
          }
        }
      }
    });
    
    // Можно добавить уведомление для пользователя
    // if (mergedCount > 0) { alert или toast }
    
  };

  // Функция для удаления выбранных элементов
  const handleDeleteSelected = () => {
    if (!timelineInstance.current || selectedItems.length === 0) return;

    const items = timelineInstance.current.itemsData;
    const deletedDbIds = [];

    selectedItems.forEach(itemId => {
      // Если это элемент из БД, добавляем его ID в список удалений
      if (originalItemsRef.current[itemId]?.dbId) {
        deletedDbIds.push(originalItemsRef.current[itemId].dbId);
      }
      items.remove(itemId);
    });

    // Обновляем список удалений
    if (deletedDbIds.length > 0) {
      setChanges(prev => ({
        ...prev,
        deleted: [...prev.deleted, ...deletedDbIds]
      }));
    }

    setSelectedItems([]);
  };

  // Функция для массового создания статусов
  const handleBulkCreate = () => {
    if (!timelineInstance.current) return;
    
    const items = timelineInstance.current.itemsData;
    const groups = timelineInstance.current.groupsData.get();
    
    const { startTime, endTime, statusType } = bulkCreateData;
    
    // Определяем, на какой тип дорожки добавлять
    const trackType = (statusType === 'online' || statusType === 'offline') ? 'main' : 'additional';
    
    // Фильтруем группы по типу дорожки
    const targetGroups = groups.filter(group => group.trackType === trackType);
    
    const newItemIds = [];
    
    // Создаем элемент для каждой группы выбранного типа
    targetGroups.forEach((group) => {
      const newItemId = `${group.deviceId}_${trackType}_${Date.now()}_${Math.random()}`;
      
      items.add({
        id: newItemId,
        group: group.id,
        originalGroup: group.id, // ВАЖНО: сохраняем оригинальную группу для защиты от перемещения
        content: statusType.toUpperCase(),
        start: startTime.toDate(),
        end: endTime.toDate(),
        className: statusType,
        editable: true,
        type: 'range',
        deviceId: group.deviceId,
        trackType: trackType
      });
      
      newItemIds.push(newItemId);
    });
    
    // Выделяем все созданные элементы
    if (timelineInstance.current) {
      timelineInstance.current.setSelection(newItemIds);
      setSelectedItems(newItemIds);
    }
    
    // Закрываем диалог
    setOpenBulkCreateDialog(false);
  };

  const handleBulkCreateDialogOpen = () => {
    // Сбрасываем значения при открытии
    setBulkCreateData({
      startTime: dayjs(),
      endTime: dayjs().add(1, 'hour'),
      statusType: 'online'
    });
    setOpenBulkCreateDialog(true);
  };

  // Функция для установки/снятия статуса "Removed"
  const handleToggleRemoved = async () => {
    if (!selectedDeviceIds || selectedDeviceIds.length === 0) {
      if (showError) showError(t('timelineNoDevices'));
      return;
    }

    try {
      if (showInfo) showInfo(t('timelineProcessing'));

      // Определяем, какие устройства уже имеют статус "Removed"
      const devicesWithRemoved = selectedDeviceIds.filter(deviceId => {
        // Проверяем в uptimeData наличие статуса "removed"
        const deviceData = snapshotUptimeData.find(d => d.device_id === deviceId);
        if (deviceData && deviceData.uptime) {
          return deviceData.uptime.some(record =>
            record.status && record.status.toLowerCase() === 'removed'
          );
        }
        return false;
      });

      const isRemoving = devicesWithRemoved.length < selectedDeviceIds.length;

      if (isRemoving) {
        // Снимаем устройства (добавляем статус "Removed" с 2025-01-01 до 2030-12-31)
        const devicesToRemove = selectedDeviceIds.filter(id => !devicesWithRemoved.includes(id));

        if (devicesToRemove.length === 0) {
          if (showInfo) showInfo(t('timelineAlreadyRemoved'));
          return;
        }

        const bulkData = {
          device_ids: devicesToRemove,
          status: 'removed',
          started_at: '2025-01-01T00:00:00Z',
          ended_at: '2030-12-31T23:59:59Z'
        };

        await api.post(`${API_BASE_URL}/device/uptime-status/bulk`, bulkData);

        if (showSuccess) showSuccess(t('timelineDevicesRemoved', { count: devicesToRemove.length }));
      } else {
        // Возвращаем устройства (удаляем статус "Removed")
        const idsToDelete = [];

        selectedDeviceIds.forEach(deviceId => {
          const deviceData = snapshotUptimeData.find(d => d.device_id === deviceId);
          if (deviceData && deviceData.uptime) {
            deviceData.uptime.forEach(record => {
              if (record.status && record.status.toLowerCase() === 'removed' && record.id) {
                idsToDelete.push(record.id);
              }
            });
          }
        });

        if (idsToDelete.length === 0) {
          if (showInfo) showInfo(t('timelineNoRemovedStatus'));
          return;
        }

        if (idsToDelete.length === 1) {
          await api.delete(`${API_BASE_URL}/device/uptime-status/${idsToDelete[0]}`);
        } else {
          await api.post(`${API_BASE_URL}/device/uptime-status/bulk-delete`, {
            ids: idsToDelete
          });
        }

        if (showSuccess) showSuccess(t('timelineDevicesReturned', { count: selectedDeviceIds.length }));
      }

      // Обновляем данные
      if (onDataRefresh) {
        await onDataRefresh();
      }

    } catch (error) {
      console.error('Ошибка при изменении статуса Removed:', error);
      const errorMsg = `Ошибка: ${error.response?.data?.error?.message || error.message}`;
      if (showError) showError(errorMsg);
    }
  };

  // Проверяем, есть ли у выбранных устройств статус "Removed"
  const hasRemovedStatus = selectedDeviceIds && selectedDeviceIds.length > 0 && snapshotUptimeData && snapshotUptimeData.length > 0 && selectedDeviceIds.some(deviceId => {
    const deviceData = snapshotUptimeData.find(d => d.device_id === deviceId);
    if (deviceData && deviceData.uptime && Array.isArray(deviceData.uptime)) {
      return deviceData.uptime.some(record =>
        record.status && typeof record.status === 'string' && record.status.toLowerCase() === 'removed'
      );
    }
    return false;
  });

  // Функция сохранения всех изменений через API
  const handleSaveChanges = async () => {
    if (!timelineInstance.current) return;

    setIsSaving(true);

    try {
      if (showInfo) showInfo(t('timelineSaving'));

      // Собираем все текущие данные из Timeline
      const items = timelineInstance.current.itemsData;
      const allItems = items.get();

      // Разделяем элементы на созданные, обновленные и удаленные
      const created = [];
      const updated = [];
      const deleted = [...changes.deleted]; // Берем из отслеживания удалений

      // Создаем Set для отслеживания обработанных оригинальных элементов
      const processedOriginalIds = new Set();

      allItems.forEach(item => {
        // Проверяем, есть ли этот элемент в оригинальных данных
        const isFromDB = originalItemsRef.current[item.id];

        if (!isFromDB) {
          // Это новый элемент
          created.push({
            device_id: item.deviceId,
            status: item.className || item.content.toLowerCase(),
            started_at: new Date(item.start).toISOString(),
            ended_at: item.end ? new Date(item.end).toISOString() : null
          });
        } else {
          // Отмечаем, что этот оригинальный элемент обработан
          processedOriginalIds.add(item.id);

          // Проверяем, изменился ли элемент
          const original = originalItemsRef.current[item.id];
          const currentStart = new Date(item.start).toISOString();
          const currentEnd = item.end ? new Date(item.end).toISOString() : null;
          const currentStatus = item.className || item.content.toLowerCase();

          // Нормализуем даты для сравнения (сравниваем с точностью до секунды)
          const normalizeDate = (dateStr) => {
            if (!dateStr) return null;
            // Округляем до секунд и убираем миллисекунды
            const date = new Date(dateStr);
            date.setMilliseconds(0);
            return date.toISOString();
          };

          const currentStartNorm = normalizeDate(currentStart);
          const currentEndNorm = normalizeDate(currentEnd);
          const originalStartNorm = normalizeDate(original.started_at);
          const originalEndNorm = normalizeDate(original.ended_at);

          // Debug для первого элемента
          if (Object.keys(originalItemsRef.current).indexOf(item.id) === 0) {
            console.log('🔍 Сравнение дат для первого элемента:', {
              itemId: item.id,
              dbId: original.dbId,
              hasDbId: !!original.dbId,
              currentStart: currentStartNorm,
              originalStart: originalStartNorm,
              currentEnd: currentEndNorm,
              originalEnd: originalEndNorm,
              currentStatus,
              originalStatus: original.status,
              startMatch: currentStartNorm === originalStartNorm,
              endMatch: currentEndNorm === originalEndNorm,
              statusMatch: currentStatus === original.status,
              hasChanges: (currentStartNorm !== originalStartNorm || currentEndNorm !== originalEndNorm || currentStatus !== original.status)
            });
          }

          const hasChanges =
            currentStartNorm !== originalStartNorm ||
            currentEndNorm !== originalEndNorm ||
            currentStatus !== original.status;

          if (hasChanges) {
            if (original.dbId) {
              // Элемент из БД - обновляем через API
              updated.push({
                id: original.dbId,
                started_at: currentStart,
                ended_at: currentEnd,
                status: currentStatus
              });
            } else {
              // Элемент БЕЗ dbId - это означает, что API не вернуло id
              // Временное решение: пытаемся найти запись по уникальным данным
              console.warn('Элемент изменен, но нет dbId! Нужно обновить API.', {
                itemId: item.id,
                original
              });

              // WORKAROUND: Если нет dbId, создаем как новый элемент и удаляем старый
              // Это НЕ идеально, но позволит работать до исправления API
              created.push({
                device_id: item.deviceId,
                status: currentStatus,
                started_at: currentStart,
                ended_at: currentEnd
              });
            }
          }
        }
      });

      // Проверяем, были ли удалены элементы (есть в originalItemsRef, но нет в allItems)
      Object.keys(originalItemsRef.current).forEach(itemId => {
        if (!processedOriginalIds.has(itemId)) {
          const original = originalItemsRef.current[itemId];
          if (original.dbId && !deleted.includes(original.dbId)) {
            deleted.push(original.dbId);
            console.log('Обнаружено удаление элемента:', { itemId, dbId: original.dbId });
          } else if (!original.dbId) {
            console.warn('Элемент удален, но нет dbId! Невозможно удалить из БД.', {
              itemId,
              original
            });
          }
        }
      });

      // Debug: выводим обнаруженные изменения
      console.log('📊 Обнаружено изменений:', {
        created: created.length,
        updated: updated.length,
        deleted: deleted.length,
        createdItems: created,
        updatedItems: updated,
        deletedIds: deleted,
        totalOriginal: Object.keys(originalItemsRef.current).length,
        totalCurrent: allItems.length
      });

      // Проверяем наличие dbId в оригинальных данных
      const hasAnyDbId = Object.values(originalItemsRef.current).some(item => item.dbId);
      if (!hasAnyDbId && Object.keys(originalItemsRef.current).length > 0) {
        const errorMsg = '⚠️ КРИТИЧЕСКАЯ ОШИБКА: API не возвращает ID записей!\n\n' +
          'Endpoint GET /device/chart/:id должен возвращать поле "id" в массиве uptime:\n\n' +
          '{\n' +
          '  "uptime": [\n' +
          '    {\n' +
          '      "id": "uuid",  ← НУЖНО ДОБАВИТЬ\n' +
          '      "status": "online",\n' +
          '      "started_at": "...",\n' +
          '      "ended_at": "..."\n' +
          '    }\n' +
          '  ]\n' +
          '}\n\n' +
          t('timelineNoIdError');

        if (showError) showError(errorMsg);
        else alert(errorMsg);

        console.error('API не возвращает ID для uptime записей!', {
          sampleOriginalItem: originalItemsRef.current[Object.keys(originalItemsRef.current)[0]]
        });

        setIsSaving(false);
        return;
      }

      // Проверяем, есть ли изменения
      if (created.length === 0 && updated.length === 0 && deleted.length === 0) {
        const message = t('timelineNoChanges');
        if (showInfo) showInfo(message);
        else alert(message);
        setIsSaving(false);
        return;
      }

      // Выполняем API запросы
      const results = {
        created: 0,
        updated: 0,
        deleted: 0,
        errors: []
      };

      // 1. Создаем новые элементы
      if (created.length > 0) {
        try {
          if (created.length === 1) {
            // Одиночное создание
            await api.post(`${API_BASE_URL}/device/uptime-status`, created[0]);
            results.created = 1;
          } else {
            // Массовое создание - группируем по device_id и параметрам
            const grouped = created.reduce((acc, item) => {
              const key = `${item.device_id}_${item.status}_${item.started_at}_${item.ended_at}`;
              if (!acc[key]) {
                acc[key] = {
                  device_ids: [],
                  status: item.status,
                  started_at: item.started_at,
                  ended_at: item.ended_at
                };
              }
              acc[key].device_ids.push(item.device_id);
              return acc;
            }, {});

            for (const bulkData of Object.values(grouped)) {
              const response = await api.post(`${API_BASE_URL}/device/uptime-status/bulk`, bulkData);
              results.created += response.data.created_count || bulkData.device_ids.length;
            }
          }
        } catch (error) {
          console.error('Ошибка создания:', error);
          results.errors.push(`Создание: ${error.response?.data?.error?.message || error.message}`);
        }
      }

      // 2. Обновляем измененные элементы
      if (updated.length > 0) {
        try {
          if (updated.length === 1) {
            // Одиночное обновление
            const item = updated[0];
            await api.put(`${API_BASE_URL}/device/uptime-status/${item.id}`, {
              started_at: item.started_at,
              ended_at: item.ended_at,
              status: item.status
            });
            results.updated = 1;
          } else {
            // Массовое обновление
            const response = await api.patch(`${API_BASE_URL}/device/uptime-status/bulk`, {
              updates: updated
            });
            results.updated = response.data.updated_count || updated.length;
          }
        } catch (error) {
          console.error('Ошибка обновления:', error);
          results.errors.push(`Обновление: ${error.response?.data?.error?.message || error.message}`);
        }
      }

      // 3. Удаляем элементы
      if (deleted.length > 0) {
        try {
          if (deleted.length === 1) {
            // Одиночное удаление
            await api.delete(`${API_BASE_URL}/device/uptime-status/${deleted[0]}`);
            results.deleted = 1;
          } else {
            // Массовое удаление через POST
            const response = await api.post(`${API_BASE_URL}/device/uptime-status/bulk-delete`, {
              ids: deleted
            });
            results.deleted = response.data.deleted_count || deleted.length;
          }
        } catch (error) {
          console.error('Ошибка удаления:', error);
          results.errors.push(t('timelineErrorDelete', { message: error.response?.data?.error?.message || error.message }));
        }
      }

      // Показываем результаты
      if (results.errors.length > 0) {
        const message = t('timelineSaveResultErrors', {
          created: results.created,
          updated: results.updated,
          deleted: results.deleted,
          errors: results.errors.join('\n')
        });
        if (showError) showError(message);
        else alert(message);
      } else {
        const totalChanges = results.created + results.updated + results.deleted;
        if (totalChanges > 0) {
          const message = t('timelineSaveResultSuccess', {
            created: results.created,
            updated: results.updated,
            deleted: results.deleted
          });
          if (showSuccess) showSuccess(message);
          else alert(message);

          // Сбрасываем изменения
          setChanges({ created: [], updated: [], deleted: [] });

          // Вызываем callback для перезагрузки данных
          if (onDataRefresh) {
            if (showInfo) showInfo(t('timelineRefreshing'));
            await onDataRefresh();
            if (showInfo) showInfo(t('timelineRefreshed'));
          }

          // НЕ закрываем диалог, данные обновятся автоматически через props
        } else {
          const message = t('timelineNoChanges');
          if (showInfo) showInfo(message);
          else alert(message);
        }
      }

    } catch (error) {
      console.error('Save critical error:', error);
      const errorMsg = t('timelineSaveError', { message: error.message });
      if (showError) showError(errorMsg);
      else alert(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {t('timelineDialogTitle')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <Toolbar variant="dense" sx={{ borderBottom: 1, borderColor: 'divider', gap: 1, flexWrap: 'wrap' }}>
        {/* Панель навигации */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {t('timelineNavigation')}
          </Typography>
          <IconButton onClick={handleZoomIn} size="small" title={t('timelineZoomIn')}>
            <ZoomInIcon />
          </IconButton>
          <IconButton onClick={handleZoomOut} size="small" title={t('timelineZoomOut')}>
            <ZoomOutIcon />
          </IconButton>
          <IconButton onClick={handleFit} size="small" title={t('timelineFitScreen')}>
            <FitScreenIcon />
          </IconButton>
        </Box>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Панель массовых операций */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {t('timelineOperations')}
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<AddBoxIcon />}
            onClick={handleBulkCreateDialogOpen}
            title={selectedDeviceIds.length === 1 ? t('timelineCreateSingleTitle') : t('timelineBulkCreateTitle')}
          >
            {selectedDeviceIds.length === 1 ? t('timelineCreateSingle') : t('timelineBulkCreateBtn')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<MergeIcon />}
            onClick={handleMergeAdjacentItems}
            title={t('timelineMergeTooltipBtn')}
          >
            {t('timelineMergeBtn')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteSelected}
            disabled={selectedItems.length === 0}
            title={t('timelineDeleteTooltip')}
          >
            {t('timelineDeleteBtn')} {selectedItems.length > 0 && `(${selectedItems.length})`}
          </Button>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Кнопка снять/вернуть устройства */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            variant={hasRemovedStatus ? "contained" : "outlined"}
            color={hasRemovedStatus ? "primary" : "warning"}
            startIcon={hasRemovedStatus ? <RestoreIcon /> : <RemoveCircleOutlineIcon />}
            onClick={handleToggleRemoved}
            disabled={!selectedDeviceIds || selectedDeviceIds.length === 0}
            title={hasRemovedStatus
              ? t('timelineReturnTitle')
              : t('timelineRemoveTitle')
            }
          >
            {hasRemovedStatus ? t('timelineReturnBtn') : t('timelineRemoveBtn')}
          </Button>
        </Box>
        
        <Box sx={{ ml: 'auto', display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip 
            label={t('timelineDeviceCount', { count: selectedDeviceIds?.length || 0 })}
            size="small" 
            color="primary" 
            variant="outlined"
          />
          {isShiftPressedState && (
            <Chip 
              label={t('timelineMagnetActive')}
              size="small" 
              color="success"
              variant="filled"
            />
          )}
        </Box>
      </Toolbar>

      <DialogContent sx={{ p: 0, height: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {isLoadingUptime ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ ml: 2 }}>{t('timelineLoadingData')}</Typography>
          </Box>
        ) : (
          <Box
            ref={timelineRef}
            sx={{
              flex: 1,
              width: '100%',
              overflowY: 'auto',
            // Универсальный стиль для неизвестных статусов (серый цвет) - ДОЛЖЕН БЫТЬ ПЕРВЫМ
            '& .vis-item': {
              backgroundColor: theme.palette.mode === 'dark' ? '#757575' : '#bdbdbd',
              borderColor: '#757575',
              color: '#fff'
            },
            '& .vis-item.online': {
              backgroundColor: theme.palette.mode === 'dark' ? '#4caf50' : '#81c784',
              borderColor: '#4caf50',
              color: '#fff'
            },
            '& .vis-item.offline': {
              backgroundColor: theme.palette.mode === 'dark' ? '#f44336' : '#e57373',
              borderColor: '#f44336',
              color: '#fff'
            },
            '& .vis-item.repair': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2196f3' : '#64b5f6',
              borderColor: '#2196f3',
              color: '#fff'
            },
            '& .vis-item.maintenance': {
              backgroundColor: theme.palette.mode === 'dark' ? '#9c27b0' : '#ba68c8',
              borderColor: '#9c27b0',
              color: '#fff'
            },
            '& .vis-item.removed': {
              backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#9e9e9e',
              borderColor: '#000',
              color: '#fff',
              opacity: 0.7
            },
            // Стили для выделенных элементов
            '& .vis-item.vis-selected': {
              borderWidth: '3px !important',
              borderStyle: 'solid !important',
              boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main} !important`,
              outline: 'none',
              zIndex: 999
            },
            // Скрываем кнопку удаления (крестик)
            '& .vis-delete': {
              display: 'none !important'
            },
            '& .vis-item .vis-delete': {
              display: 'none !important'
            },
            '& .vis-timeline': {
              border: 'none',
              fontFamily: theme.typography.fontFamily
            },
            '& .vis-panel.vis-background': {
              backgroundColor: theme.palette.background.default
            },
            '& .vis-panel.vis-center': {
              backgroundColor: theme.palette.background.paper
            },
            '& .vis-labelset .vis-label': {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderRight: `1px solid ${theme.palette.divider}`
            },
            '& .vis-time-axis .vis-text': {
              color: theme.palette.text.secondary
            },
            '& .vis-time-axis .vis-grid.vis-vertical': {
              borderLeft: `1px solid ${theme.palette.divider}`
            },
            '& .vis-time-axis .vis-grid.vis-minor': {
              borderLeft: `1px solid ${theme.palette.divider}`,
              opacity: 0.3
            }
          }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
        {/* Легенда статусов */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', px: 2 }}>
          <Chip 
            label="Online" 
            size="small" 
            sx={{ 
              backgroundColor: theme.palette.mode === 'dark' ? '#4caf50' : '#81c784',
              color: '#fff'
            }}
          />
          <Chip 
            label="Offline" 
            size="small" 
            sx={{ 
              backgroundColor: theme.palette.mode === 'dark' ? '#f44336' : '#e57373',
              color: '#fff'
            }}
          />
          <Chip 
            label="Repair" 
            size="small" 
            sx={{ 
              backgroundColor: theme.palette.mode === 'dark' ? '#2196f3' : '#64b5f6',
              color: '#fff'
            }}
          />
          <Chip
            label="Maintenance"
            size="small"
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#9c27b0' : '#ba68c8',
              color: '#fff'
            }}
          />
          <Chip
            label="Removed"
            size="small"
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#9e9e9e',
              color: '#fff',
              opacity: 0.7
            }}
          />
          <Chip
            label={t('timelineLegendOther')}
            size="small"
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#757575' : '#bdbdbd',
              color: '#fff'
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('timelineHintCtrl')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('timelineHintShift')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveChanges}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            >
              {isSaving ? t('timelineSaveInProgress') : t('timelineSaveBtn')}
            </Button>
            <Button onClick={onClose} disabled={isSaving}>{t('timelineCloseBtn')}</Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>

    {/* Диалог массового создания статусов */}
    <Dialog 
      open={openBulkCreateDialog} 
      onClose={() => setOpenBulkCreateDialog(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{selectedDeviceIds.length === 1 ? t('timelineCreateDialogTitle') : t('timelineBulkCreateDialogTitle')}</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedDeviceIds.length === 1
                ? t('timelineCreateDesc')
                : t('timelineBulkCreateDesc')
              }
            </Typography>
            
            <FormControl fullWidth size="small">
              <InputLabel>{t('timelineStatusType')}</InputLabel>
              <Select
                value={bulkCreateData.statusType}
                onChange={(e) => setBulkCreateData({ ...bulkCreateData, statusType: e.target.value })}
                label={t('timelineStatusType')}
              >
                <MenuItem value="online">
                  <Chip 
                    label="Online" 
                    size="small" 
                    sx={{ 
                      backgroundColor: theme.palette.mode === 'dark' ? '#4caf50' : '#81c784',
                      color: '#fff',
                      mr: 1
                    }}
                  />
                  {t('timelineMainTrackLabel')}
                </MenuItem>
                <MenuItem value="offline">
                  <Chip
                    label="Offline"
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.mode === 'dark' ? '#f44336' : '#e57373',
                      color: '#fff',
                      mr: 1
                    }}
                  />
                  {t('timelineMainTrackLabel')}
                </MenuItem>
                <MenuItem value="repair">
                  <Chip
                    label="Repair"
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.mode === 'dark' ? '#2196f3' : '#64b5f6',
                      color: '#fff',
                      mr: 1
                    }}
                  />
                  {t('timelineAdditionalTrackLabel')}
                </MenuItem>
                <MenuItem value="maintenance">
                  <Chip
                    label="Maintenance"
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.mode === 'dark' ? '#9c27b0' : '#ba68c8',
                      color: '#fff',
                      mr: 1
                    }}
                  />
                  {t('timelineAdditionalTrackLabel')}
                </MenuItem>
                <MenuItem value="removed">
                  <Chip
                    label="Removed"
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#9e9e9e',
                      color: '#fff',
                      opacity: 0.7,
                      mr: 1
                    }}
                  />
                  {t('timelineAdditionalTrackLabel')}
                </MenuItem>
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label={t('timelineStartLabel')}
                  value={bulkCreateData.startTime}
                  onChange={(newValue) => setBulkCreateData({ ...bulkCreateData, startTime: newValue })}
                  ampm={false}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small"
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label={t('timelineEndLabel')}
                  value={bulkCreateData.endTime}
                  onChange={(newValue) => setBulkCreateData({ ...bulkCreateData, endTime: newValue })}
                  ampm={false}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small"
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ 
              p: 2, 
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              borderRadius: 1 
            }}>
              <Typography variant="caption" color="text.secondary">
                <strong>{t('timelineWillCreate')}</strong> {t('timelineItemsCount', { count: selectedDeviceIds?.length || 0 })}<br/>
                <strong>{t('timelineTrackLabelText')}</strong> {
                  (bulkCreateData.statusType === 'online' || bulkCreateData.statusType === 'offline')
                    ? t('timelineTrackMainLabel')
                    : t('timelineTrackAdditionalLabel')
                }
              </Typography>
            </Box>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenBulkCreateDialog(false)}>{t('timelineCancelBtn')}</Button>
        <Button 
          onClick={handleBulkCreate} 
          variant="contained" 
          color="success"
          disabled={!bulkCreateData.startTime || !bulkCreateData.endTime || bulkCreateData.startTime >= bulkCreateData.endTime}
        >
          {t('timelineCreateActionBtn')}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default DeviceStatusTimeline;

